import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { getShareChat } from "../../lib/redux/actions";
import Markdown from "markdown-to-jsx";
import FileCard from "./FileCard";
import { Copy, Check, Mic, Download } from "lucide-react";
import BotMarkdown from "../../lib/common/components/markdown/BotMarkdown";
import CustomTooltip from "../ui/Tooltip";
import Avatar from "../../assets/svg/avatar.svg";
import daburLogo from "../../assets/svg/appIcon.svg";
import toast from "../../lib/utils/toast";
import SkeletonLoader from "../../lib/common/components/loader/SkeletonLoader";
import { preprocessText, CustomLink } from "../../lib/utils/helper";

export default function ReadOnlyChat() {
  const { sessionId } = useParams();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedMessageId, setCopiedMessageId] = useState(null);

  const scrollRef = useRef(null);

  const fetchChats = async (sessionId) => {
    setLoading(true);
    try {
      const res = await getShareChat(sessionId);

      setChats(res?.data?.data || []);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error.message ||
        "Failed to load chat";
      toast.error(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchChats(sessionId);
  }, [sessionId]);

  const handleCopyText = (text, messageId) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedMessageId(messageId);
        setTimeout(() => {
          setCopiedMessageId(null);
        }, 1000);
      })
      .catch(() => {
        toast.error("Failed to copy text");
      });
  };

  if (loading) return <SkeletonLoader />;

  return (
    <div className="flex-1 flex flex-col overflow-scroll sm:overflow-hidden">
      <div
        id="scrollableDiv"
        ref={scrollRef}
        className="flex-1 py-4 !px-[30px] sm:px-[50px] md:px-[20px] lg:px-[100px] xl:!px-[220px] 2xl:!px-[220px] overflow-y-auto flex flex-col-reverse mt-[40px]"
      >
        {chats.length === 0 && (
          <p className="text-center text-gray-500">No chats found.</p>
        )}
        {chats.map((chat, i) =>
          chat.role === "user" ? (
            <div
              key={chat._id || `chat-${i}`}
              className={`flex group justify-end ${
                i === 0 ? "mt-12 mb-8 " : "mt-8 mb-8"
              }`}
            >
              <div className="flex flex-row gap-2">
                <div className="flex flex-col ">
                  <div className="bg-[#1b5c2926] text-[#000000] flex flex-col gap-2 w-fit overflow-x-auto rounded-bl-[16px] rounded-br-[16px] rounded-tl-[16px] p-[14px] min-h-[44px] justify-center group">
                    <div className="markDownWrapper !leadings-[27px] font-hel leading-[27px] w-fit whitespace-pre-line break-words font-medium text-sm ">
                      <Markdown
                        key={`chat-${chat?._id}`}
                        options={{
                          overrides: {
                            a: { component: CustomLink },
                          },
                        }}
                      >
                        {preprocessText(chat.message)}
                      </Markdown>
                    </div>
                    {chat.media && chat.media.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {chat.media.map((mediaItem, fileIndex) => (
                          <FileCard
                            index={i}
                            key={`filecard-${mediaItem}`}
                            mediaItem={mediaItem}
                            fileIndex={fileIndex}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <button
                      onClick={() =>
                        handleCopyText(chat.message, chat._id || `chat-${i}`)
                      }
                      className="flex  cursor-pointer items-center gap-1 text-gray-600 p-1 rounded-md hover:bg-[#1b5c2926] opacity-0 group-hover:opacity-100 transition-opacity text-sm  self-start"
                    >
                      <CustomTooltip
                        bgColor={"#000000"}
                        textColor={"#ffffff"}
                        data-tooltip-id={`copy-${
                          chat._id || `chat-${i}`
                        }-tooltip`}
                        data-tooltip-content={
                          copiedMessageId === (chat._id || `chat-${i}`)
                            ? "Copied!"
                            : "Copy"
                        }
                        delay={0}
                        show={true}
                      >
                        {copiedMessageId === (chat._id || `chat-${i}`) ? (
                          <Check color="#000000B2" size={17} />
                        ) : (
                          <Copy color="#000000B2" size={17} />
                        )}
                      </CustomTooltip>
                    </button>
                    {chat?.isAudio && (
                      <div className="p-1 ">
                        <Mic className="text-[#000000B2]" size={16} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="!w-[35px] flex-shrink-0">
                  <img src={Avatar} alt="User" className="w-[40px]" />
                </div>
              </div>
            </div>
          ) : (
            <div key={chat._id} className="mt-8 mb-8 max-w-4xl space-y-8">
              <div className="flex gap-4 w-[280px] sm:w-full">
                <div className="w-[40px]">
                  <img src={daburLogo} alt="Bot" className="w-[40px]" />
                </div>
                <div className="text-[#000000] flex flex-col gap-2 overflow-x-auto rounded-bl-lg rounded-br-lg rounded-tr-lg min-h-[44px] justify-center w-fit group">
                  <div className="markDownWrapper !leadings-[27px] font-hel w-fit whitespace-pre-line break-words font-medium text-md ">
                    <BotMarkdown content={preprocessText(chat.message)} />
                    {chat?.isDownloadable && (
                      <button
                        className={`flex items-center gap-3 py-2 rounded-lg transition `}
                        onClick={() => {
                          if (chat?.fileDownloadLink) {
                            window.open(chat.fileDownloadLink, "_blank");
                          } else {
                            toast.error("File not available for download");
                          }
                        }}
                      >
                        <div className={`bg-white p-3 rounded-full`}>
                          <Download className="w-5 h-5 text-green-800" />
                        </div>
                        <span className="text-base text-[#1B5C29] font-medium">
                          Download File
                        </span>
                      </button>
                    )}
                    <button
                      onClick={() => handleCopyText(chat.message, chat._id)}
                      className="flex cursor-pointer items-center gap-1 text-gray-600 hover:bg-[#1b5c2926] p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity text-sm mt-2 self-end"
                    >
                      <CustomTooltip
                        bgColor={"#000000"}
                        textColor={"#ffffff"}
                        data-tooltip-id={`copy-${chat._id}-tooltip`}
                        data-tooltip-content={
                          copiedMessageId === chat._id ? "Copied!" : "Copy"
                        }
                        show={true}
                        delay={0}
                      >
                        {copiedMessageId === chat._id ? (
                          <Check color="#000000B2" size={17} />
                        ) : (
                          <Copy color="#000000B2" size={17} />
                        )}
                      </CustomTooltip>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}
